import React, { useEffect, useRef, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Chip,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  ThemeProvider,
  Toolbar,
  Tooltip,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import MovieIcon from "@mui/icons-material/Movie";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Canvas from "./Canvas";
import { GAUGE_LIST, gaugePreview, renderVideo, parseLog } from "./render";
import { LAYOUTS } from "./layouts";
import "./App.css";

const DRAWER_WIDTH = 340;

const GithubCorner = () => (
  <a
    href="https://github.com/LucasTor/fueltech-video-osd"
    target="_blank"
    rel="noopener noreferrer"
    className="github-corner"
    aria-label="View source on GitHub"
  >
    <svg
      width="72"
      height="72"
      viewBox="0 0 250 250"
      style={{
        fill: "#e30613",
        color: "#0e1013",
        position: "fixed",
        top: 0,
        right: 0,
        zIndex: 1300,
      }}
      aria-hidden="true"
    >
      <path d="M0,0 L115,115 L130,115 L142,142 L250,250 L250,0 Z" />
      <path
        d="M128.3,109.0 C113.8,99.7 119.0,89.6 119.0,89.6 C122.0,82.7 120.5,78.6 120.5,78.6 C119.2,72.0 123.4,76.3 123.4,76.3 C127.3,80.9 125.5,87.3 125.5,87.3 C122.9,97.6 130.6,101.9 134.4,103.2"
        fill="currentColor"
        style={{ transformOrigin: "130px 106px" }}
        className="octo-arm"
      />
      <path
        d="M115.0,115.0 C114.9,115.1 118.7,116.5 119.8,115.4 L133.7,101.6 C136.9,99.2 139.9,98.4 142.2,98.6 C133.8,88.0 127.5,74.4 143.8,58.0 C148.5,53.4 154.0,51.2 159.7,51.0 C160.3,49.4 163.2,43.6 171.4,40.1 C171.4,40.1 176.1,42.5 178.8,56.2 C183.1,58.6 187.2,61.8 190.9,65.4 C194.5,69.0 197.7,73.2 200.1,77.6 C213.8,80.2 216.3,84.9 216.3,84.9 C212.7,93.1 206.9,96.0 205.4,96.6 C205.1,102.4 203.0,107.8 198.3,112.5 C181.9,128.9 168.3,122.5 157.7,114.1 C157.9,116.9 156.7,120.9 152.7,124.9 L141.0,136.5 C139.8,137.7 141.6,141.9 141.8,141.8 Z"
        fill="currentColor"
        className="octo-body"
      />
    </svg>
  </a>
);

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#e30613" },
    background: { default: "#0e1013", paper: "#16191d" },
  },
  typography: {
    fontFamily: "Roboto, sans-serif",
  },
});

// v2: gauge geometry changed when the drawn gauge set replaced the PNG assets
const STORAGE_KEY = "fueltech-osd-layout-v2";

function layoutRectangles(layout) {
  return GAUGE_LIST.map((g) => {
    const override = layout.gauges[g.id];
    return {
      id: g.id,
      name: g.name,
      img: gaugePreview(g.id),
      x: 40,
      y: 40,
      width: g.width,
      height: g.height,
      enabled: false,
      ...(override ? { ...override, enabled: true } : {}),
    };
  });
}

function loadSavedLayout() {
  const defaults = layoutRectangles(LAYOUTS[0]);
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!Array.isArray(saved)) return defaults;
    // Merge saved geometry onto fresh defaults so new gauge types and
    // regenerated previews always come from the current registry
    return defaults.map((r) => {
      const s = saved.find((x) => x.id === r.id);
      return s ? { ...r, ...s, img: r.img, name: r.name } : r;
    });
  } catch {
    return defaults;
  }
}

// Feature-detect the browser APIs the exporter needs. VideoEncoder (WebCodecs)
// is a hard requirement; AudioEncoder is optional — without it the export
// still works but drops the clip's audio track.
function browserSupport() {
  const hasVideo = typeof window.VideoEncoder !== "undefined";
  const hasAudio = typeof window.AudioEncoder !== "undefined";
  return { hasVideo, hasAudio, exportOk: hasVideo };
}

const SupportRow = ({ ok, optional, label, detail }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    {ok ? (
      <CheckCircleOutlineIcon fontSize="small" color="success" sx={{ mt: 0.25 }} />
    ) : optional ? (
      <WarningAmberIcon fontSize="small" color="warning" sx={{ mt: 0.25 }} />
    ) : (
      <ErrorOutlineIcon fontSize="small" color="error" sx={{ mt: 0.25 }} />
    )}
    <Box>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="caption" color="text.secondary">
        {detail}
      </Typography>
    </Box>
  </Stack>
);

// Shown on load (and again if "Generate" is pressed) when the browser is
// missing the APIs video export needs. Editing still works everywhere, so the
// modal is dismissible rather than blocking.
function SupportDialog({ open, onClose, support, fullScreen }) {
  const { hasVideo, hasAudio } = support;
  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="xs">
      <DialogTitle>
        {hasVideo
          ? "Limited browser support"
          : "This browser can't export video"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <SupportRow
              ok={hasVideo}
              label="Video encoding (WebCodecs)"
              detail={
                hasVideo
                  ? "Hardware H.264 encoding is available."
                  : "Required to generate the MP4. Not available in this browser."
              }
            />
            <SupportRow
              ok={hasAudio}
              optional
              label="Audio encoding"
              detail={
                hasAudio
                  ? "Your clip's audio is kept in the export."
                  : "Optional — exports will work but lose the clip's audio."
              }
            />
            <Typography variant="body2">
              You can still lay out gauges, load logs and preview everything
              here. To export, open this page in a recent{" "}
              <strong>Chrome</strong> or <strong>Edge</strong> (desktop or
              Android).
            </Typography>
          </Stack>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// The editing controls. Rendered inside a permanent sidebar on desktop and a
// slide-out Drawer on mobile, so it takes everything it needs as props.
function SidebarControls({
  rectangles,
  setRectangles,
  showReelsUI,
  setShowReelsUI,
  logInfo,
  videoInfo,
  fileInputRef,
  videoInputRef,
  missingColumn,
  progress,
  log,
  generate,
  isMobile,
  onClose,
}) {
  const rowPad = isMobile ? 1.5 : 1;
  return (
    <Box
      sx={{
        width: { xs: "100%", md: DRAWER_WIDTH },
        flexShrink: 0,
        p: 3,
        pt: { xs: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 2,
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        bgcolor: "background.paper",
        borderRight: { xs: 0, md: "1px solid" },
        borderColor: { md: "divider" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
            FuelTech Video OSD
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Overlay FuelTech's datalogs on your video
          </Typography>
        </Box>
        {isMobile && (
          <IconButton onClick={onClose} edge="end" aria-label="Close menu">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Divider />

      <Typography variant="overline" color="text.secondary">
        Layout presets
      </Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
        {LAYOUTS.map((l) => (
          <Tooltip key={l.id} title={l.description}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              sx={{ borderColor: "divider", textTransform: "none" }}
              onClick={() => setRectangles(layoutRectangles(l))}
            >
              {l.name}
            </Button>
          </Tooltip>
        ))}
      </Stack>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={showReelsUI}
            onChange={(_, checked) => setShowReelsUI(checked)}
          />
        }
        label={
          <Typography variant="body2" color="text.secondary">
            Show Reels UI guide
          </Typography>
        }
      />

      <Divider />

      <Typography variant="overline" color="text.secondary">
        Gauges
      </Typography>
      <Stack spacing={isMobile ? 1.5 : 1}>
        {rectangles.map((r, i) => (
          <Stack
            key={r.id}
            direction="row"
            alignItems="center"
            spacing={1.5}
            onClick={() =>
              setRectangles((prev) => {
                const next = [...prev];
                next[i] = { ...next[i], enabled: !next[i].enabled };
                return next;
              })
            }
            sx={{
              p: rowPad,
              borderRadius: 1.5,
              cursor: "pointer",
              bgcolor: r.enabled ? "rgba(227, 6, 19, 0.08)" : "transparent",
              "&:hover": {
                bgcolor: r.enabled
                  ? "rgba(227, 6, 19, 0.14)"
                  : "rgba(255, 255, 255, 0.04)",
              },
            }}
          >
            <Box
              component="img"
              src={r.img}
              alt=""
              sx={{ width: 44, height: 36, objectFit: "contain" }}
            />
            <Typography sx={{ flex: 1 }}>{r.name}</Typography>
            {missingColumn(r.id) && (
              <Tooltip title="The loaded log has no column for this gauge">
                <Chip size="small" label="no data" color="warning" variant="outlined" />
              </Tooltip>
            )}
            <Switch
              checked={!!r.enabled}
              onClick={(e) => e.stopPropagation()}
              onChange={(_, checked) =>
                setRectangles((prev) => {
                  const next = [...prev];
                  next[i] = { ...next[i], enabled: checked };
                  return next;
                })
              }
            />
          </Stack>
        ))}
      </Stack>

      <Divider />

      <Typography variant="overline" color="text.secondary">
        Data log
      </Typography>
      <Button
        variant="outlined"
        startIcon={<UploadFileIcon />}
        onClick={() => fileInputRef.current.click()}
      >
        {logInfo ? "Change log" : "Load log CSV"}
      </Button>
      {logInfo && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" label={logInfo.name} />
          <Chip
            size="small"
            variant="outlined"
            label={`${(logInfo.rows.length / 25).toFixed(1)}s · ${logInfo.rows.length} samples`}
          />
        </Stack>
      )}

      <Typography variant="overline" color="text.secondary">
        Camera video
      </Typography>
      <Button
        variant="outlined"
        startIcon={<VideoFileIcon />}
        onClick={() => videoInputRef.current.click()}
      >
        {videoInfo ? "Change video" : "Load video"}
      </Button>
      {videoInfo ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" label={videoInfo.name} />
          <Chip
            size="small"
            variant="outlined"
            label={`${videoInfo.duration.toFixed(1)}s`}
          />
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Optional — without footage the video renders on the green screen
          background
        </Typography>
      )}

      <Box sx={{ flexGrow: 1, minHeight: 8 }} />

      {progress !== null && (
        <LinearProgress variant="determinate" value={progress * 100} />
      )}
      <Tooltip title={log ? "" : "Load a log file first"}>
        <span>
          <Button
            fullWidth
            size="large"
            variant="contained"
            startIcon={<MovieIcon />}
            disabled={!log || progress !== null}
            onClick={generate}
          >
            {progress !== null
              ? `Rendering ${Math.round(progress * 100)}%`
              : "Generate video"}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}

function App() {
  const [rectangles, setRectangles] = useState(loadSavedLayout);
  const [log, setLog] = useState(null);
  const [logInfo, setLogInfo] = useState(null);
  const [videoInfo, setVideoInfo] = useState(null);
  const [offset, setOffset] = useState(0);
  const [progress, setProgress] = useState(null);
  const [showReelsUI, setShowReelsUI] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [support] = useState(browserSupport);
  const [supportOpen, setSupportOpen] = useState(!support.exportOk);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rectangles));
  }, [rectangles]);

  function readFile(input) {
    const file = input.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => {
      try {
        const { rows, columns } = parseLog(reader.result);
        if (!rows.length) throw new Error("empty log");
        setLog(reader.result);
        setLogInfo({ name: file.name, rows, columns });
        toast.success(`Loaded ${file.name} — ${rows.length} samples`);
      } catch {
        toast.error("Couldn't parse this file as a FuelTech CSV log");
      }
    };
    reader.onerror = () => toast.error("Couldn't read the file");
  }

  function readVideo(input) {
    const file = input.target.files[0];
    if (!file) return;
    const el = document.createElement("video");
    el.src = URL.createObjectURL(file);
    el.playsInline = true;
    el.preload = "auto";
    el.onloadedmetadata = () => {
      setVideoInfo({ file, el, name: file.name, duration: el.duration });
      setOffset(0);
      toast.success(`Loaded ${file.name} — ${el.duration.toFixed(1)}s`);
    };
    el.onerror = () => toast.error("Couldn't load this video file");
  }

  async function generate() {
    if (!support.exportOk) {
      setSupportOpen(true);
      return;
    }
    try {
      setDrawerOpen(false);
      videoInfo?.el.pause();
      setProgress(0);
      const blob = await renderVideo({
        gauges: rectangles.filter((r) => r.enabled),
        log,
        videoFile: videoInfo?.file,
        offset,
        onProgress: setProgress,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "overlay.mp4");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("overlay.mp4 downloaded");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setProgress(null);
    }
  }

  const missingColumn = (id) => logInfo && logInfo.columns[id] === -1;

  const sidebar = (
    <SidebarControls
      rectangles={rectangles}
      setRectangles={setRectangles}
      showReelsUI={showReelsUI}
      setShowReelsUI={setShowReelsUI}
      logInfo={logInfo}
      videoInfo={videoInfo}
      fileInputRef={fileInputRef}
      videoInputRef={videoInputRef}
      missingColumn={missingColumn}
      progress={progress}
      log={log}
      generate={generate}
      isMobile={isMobile}
      onClose={() => setDrawerOpen(false)}
    />
  );

  const canvas = (
    <Canvas
      rectangles={rectangles}
      setRectangles={setRectangles}
      showReelsUI={showReelsUI}
      videoEl={videoInfo?.el || null}
      rows={logInfo?.rows || null}
      columns={logInfo?.columns || null}
      offset={offset}
      setOffset={setOffset}
      isMobile={isMobile}
    />
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* The hidden file inputs live at the app root so they work whether the
          controls are in the desktop sidebar or the mobile drawer. */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={readFile}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        hidden
        onChange={readVideo}
      />

      {isMobile ? (
        <Box
          className="app-shell"
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AppBar
            position="static"
            color="default"
            elevation={0}
            sx={{
              bgcolor: "background.paper",
              borderBottom: "1px solid",
              borderColor: "divider",
              pt: "env(safe-area-inset-top)",
            }}
          >
            <Toolbar variant="dense" sx={{ gap: 1 }}>
              <IconButton
                edge="start"
                onClick={() => setDrawerOpen(true)}
                aria-label="Open menu"
              >
                <MenuIcon />
              </IconButton>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, flex: 1, letterSpacing: 0.3 }}
                noWrap
              >
                FuelTech Video OSD
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<MovieIcon />}
                disabled={!log || progress !== null}
                onClick={generate}
                sx={{ flexShrink: 0 }}
              >
                {progress !== null ? `${Math.round(progress * 100)}%` : "Render"}
              </Button>
            </Toolbar>
            {progress !== null && (
              <LinearProgress variant="determinate" value={progress * 100} />
            )}
          </AppBar>

          <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>{canvas}</Box>

          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: { xs: "88vw", sm: DRAWER_WIDTH },
                maxWidth: DRAWER_WIDTH,
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            {sidebar}
          </Drawer>
        </Box>
      ) : (
        <>
          <GithubCorner />
          <Box className="app-shell" sx={{ display: "flex" }}>
            {sidebar}
            {canvas}
          </Box>
        </>
      )}
      <SupportDialog
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        support={support}
        fullScreen={false}
      />
      <ToastContainer position="bottom-right" theme="dark" />
    </ThemeProvider>
  );
}

export default App;
